import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, ShieldAlert, GraduationCap, IdCard, Calendar, Award,
  ArrowRight, User as UserIcon, MapPin, Sparkles, Heart,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function Verify() {
  const { kind, code } = useParams(); // kind: 'certificado' | 'carteirinha'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const endpoint = kind === "certificado" ? "certificate" : "carteirinha";
    axios.get(`${API}/verify/${endpoint}/${code}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ valid: false }))
      .finally(() => setLoading(false));
  }, [kind, code]);

  if (loading) {
    return (
      <div className="container-cuidar py-24 text-center">
        <div className="animate-pulse mx-auto h-14 w-14 rounded-full bg-[hsl(var(--muted))]" />
        <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Verificando autenticidade…</div>
      </div>
    );
  }

  if (!data?.valid) {
    return (
      <div>
        <section className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40">
          <div className="container-cuidar py-14 text-center max-w-2xl mx-auto">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-100 grid place-items-center text-red-600">
              <ShieldAlert className="h-8 w-8" />
            </div>
            <h1 className="mt-5 font-heading text-3xl sm:text-4xl font-black">Documento não encontrado</h1>
            <p className="mt-3 text-[hsl(var(--muted-foreground))]" data-testid="verify-invalid">
              Este código não corresponde a nenhum documento oficial do Instituto Cuidar.
              Pode ser um documento inválido, cancelado ou ainda não emitido.
            </p>
            <div className="mt-6 rounded-2xl bg-white border border-[hsl(var(--border))] px-5 py-3 text-sm inline-flex items-center gap-2 font-mono">
              <span className="text-[hsl(var(--muted-foreground))]">Código:</span>
              <span className="font-bold">{code}</span>
            </div>
            <div className="mt-8">
              <Link to="/">
                <Button variant="outline" className="rounded-full">Voltar ao início</Button>
              </Link>
            </div>
            <div className="mt-10 rounded-2xl bg-[hsl(var(--secondary))]/8 border border-[hsl(var(--secondary))]/25 p-5 text-left">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--secondary))] flex items-center gap-1.5">
                <Heart className="h-3 w-3" /> Sobre o Instituto Cuidar
              </div>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                Oferecemos apoio gratuito a famílias de crianças neuroatípicas em todo o Brasil.
                Já que você chegou até aqui, que tal conhecer o instituto e apoiar quem cuida?
              </p>
              <Link to="/doacoes" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[hsl(var(--secondary))]">
                Fazer uma doação <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const isCert = kind === "certificado";
  const Icon = isCert ? Award : IdCard;
  const label = isCert ? "Certificado autêntico" : "Carteirinha autêntica";

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--primary))]/8 via-white to-[hsl(var(--secondary))]/8">
        <div className="container-cuidar py-14">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--primary))]/25 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[hsl(var(--primary))]">
              <ShieldCheck className="h-3.5 w-3.5" /> {label}
            </div>
            <div className="mx-auto mt-6 h-20 w-20 rounded-3xl bg-[hsl(var(--primary))] grid place-items-center text-white shadow-lg">
              <Icon className="h-9 w-9" />
            </div>
            <h1 className="mt-6 font-heading text-3xl sm:text-4xl font-black" data-testid="verify-title">
              {isCert ? "Certificado verificado" : "Carteirinha verificada"}
            </h1>
            <p className="mt-3 text-[hsl(var(--muted-foreground))]" data-testid="verify-valid">
              Este documento foi emitido pelo <b>Instituto Cuidar</b> e é autêntico.
            </p>
          </div>

          <div className="mt-10 max-w-2xl mx-auto surface p-8">
            {isCert ? <CertificateBody data={data} /> : <CarteirinhaBody data={data} />}
          </div>

          {/* Prominent Donation CTA — every verification is a chance to convert */}
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[hsl(var(--secondary))] to-[hsl(var(--secondary))]/85 text-white p-8 md:p-10 shadow-xl">
              <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" aria-hidden />
              <div className="absolute -bottom-12 -left-12 h-52 w-52 rounded-full bg-white/10 blur-3xl" aria-hidden />
              <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="h-16 w-16 rounded-2xl bg-white/20 grid place-items-center shrink-0 backdrop-blur">
                  <Heart className="h-8 w-8 fill-white" />
                </div>
                <div className="flex-1">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-white/70">Faça parte</div>
                  <h3 className="mt-1 font-heading text-2xl md:text-3xl font-black leading-tight">
                    Sua doação transforma o dia a dia<br className="hidden md:block" /> de crianças reais
                  </h3>
                  <p className="mt-3 text-sm text-white/90 max-w-md">
                    O Instituto Cuidar oferece cursos, orientação jurídica e acolhimento para famílias
                    de todo o Brasil — 100% gratuito. Isso só é possível com o apoio de pessoas como você.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <Link to="/doacoes">
                      <Button className="btn-pill h-12 px-6 bg-white text-[hsl(var(--secondary))] hover:bg-white/95 font-bold shadow-md" data-testid="verify-donate-btn">
                        <Heart className="h-4 w-4 mr-1.5 fill-[hsl(var(--secondary))]" /> Doar agora
                      </Button>
                    </Link>
                    <Link to="/apoiar">
                      <Button variant="outline" className="btn-pill h-12 px-6 bg-transparent border-white/40 text-white hover:bg-white/10" data-testid="verify-support-btn">
                        Ser voluntário(a) <ArrowRight className="h-4 w-4 ml-1.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 max-w-2xl mx-auto rounded-3xl bg-[hsl(var(--primary))] text-white p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-white/70">
                <Sparkles className="h-3.5 w-3.5 inline mr-1" /> Sobre o Instituto Cuidar
              </div>
              <h3 className="mt-2 font-heading text-2xl font-black">Assistência a crianças neuroatípicas</h3>
              <p className="mt-2 text-sm text-white/85 max-w-md">
                Rede de apoio, direitos, cursos e acolhimento para famílias em todo o Brasil.
              </p>
            </div>
            <Link to="/">
              <Button className="btn-pill h-12 px-6 bg-white text-[hsl(var(--primary))] hover:bg-white/95 shrink-0" data-testid="verify-cta-home">
                Conhecer o Instituto <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function CertificateBody({ data }) {
  return (
    <>
      <Row icon={UserIcon} label="Concluído por" value={data.user_name} testid="verify-cert-user" />
      <Row icon={GraduationCap} label="Curso" value={data.course_title} testid="verify-cert-course" />
      <Row icon={Calendar} label="Emitido em" value={fmtDate(data.issued_at)} testid="verify-cert-date" />
      <Row icon={Award} label="Código do certificado" value={data.code} mono testid="verify-cert-code" />
    </>
  );
}

function CarteirinhaBody({ data }) {
  return (
    <>
      <Row icon={UserIcon} label="Responsável" value={data.responsible_name} testid="verify-cart-responsible" />
      {data.child_name && <Row icon={UserIcon} label="Criança" value={data.child_name} testid="verify-cart-child" />}
      {data.child_condition && <Row icon={Sparkles} label="Situação neuroatípica" value={data.child_condition} testid="verify-cart-condition" />}
      {data.city_state && <Row icon={MapPin} label="Cidade / UF" value={data.city_state} testid="verify-cart-location" />}
      {data.issued_year && <Row icon={Calendar} label="Cadastrado(a) em" value={String(data.issued_year)} testid="verify-cart-year" />}
      <Row icon={IdCard} label="Código da carteirinha" value={data.code} mono testid="verify-cart-code" />
    </>
  );
}

function Row({ icon: Icon, label, value, mono = false, testid }) {
  return (
    <div className="py-4 border-t border-[hsl(var(--border))] first:border-t-0 first:pt-0 last:pb-0 grid grid-cols-[36px_1fr] gap-3 items-start" data-testid={testid}>
      <div className="h-9 w-9 rounded-xl bg-[hsl(var(--primary))]/10 grid place-items-center text-[hsl(var(--primary))]">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">{label}</div>
        <div className={`mt-0.5 font-heading font-extrabold ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
      </div>
    </div>
  );
}
